import React, { useCallback, useContext, useEffect, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { Slide, toast } from 'react-toastify';
import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import ContactsContextProvider from '~/components/ContactsContext';
import { MinimizedDialogContextProvider } from '~/components/core/MinimizedBar/Context';
import ClaimPageNavigationBlocker from '~/components/core/MinimizedBar/Context/ClaimPageNavigationBlocker';
import { useCms } from '~/components/hooks/useCms';
import LoadingIndicator from '~/components/LoadingIndicator';
import { PolicyContextProvider } from '~/components/PolicyContainer';
import RefreshToaster from '~/components/RefreshToaster';
import type { ClaimPolymorph, PopulatedClaimWithOrganization } from '~/components/types/claim-types';
import type { SubOrganizationModel } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';
import { touchClaim } from '~/Utils/ClaimUtils';
import { isPermissionDeniedError } from '~/Utils/errors/permissionDenined';
import { populateClaim, type PopulatedClaim } from '~/Utils/populateClaim';

// Types
interface ClaimContextType {
  claim?: PopulatedClaim;
  refreshData?: () => Promise<PopulatedClaim | void>;
  onClaimUpdate?: () => Promise<PopulatedClaim | void>;
  asyncRefreshData?: () => void;
  onAsyncClaimUpdate?: () => void;
  inClaimPage?: boolean;
  reloadingUuid?: string | null;
}

// When claim is required, expose a stricter type
type NonNullableAndRequired<T> = { [K in keyof T]-?: NonNullable<T[K]> };
type ClaimContextTypeRequired = NonNullableAndRequired<ClaimContextType>;

interface ClaimContainerOwnProps {
  claimId: number;
  children: React.ReactNode;
}

type ClaimContainerProps = ClaimContainerOwnProps & RouteComponentProps;

interface ClaimContextProviderProps {
  claim: PopulatedClaimWithOrganization;
  refreshData: () => Promise<PopulatedClaim | void>;
  asyncRefreshData?: () => void;
  inClaimPage?: boolean;
  children: NonNullable<React.ReactNode>;
  reloadingUuid?: string;
  createMinimizedContext?: boolean;
}

// Context Record<string, never> is used to avoid type errors when the context is not initialized, and to not change legacy behaviour
const ClaimContext = React.createContext<ClaimContextType>({});

function ClaimContainer({ claimId, children, history }: ClaimContainerProps) {
  const [claim, setClaim] = useState<PopulatedClaimWithOrganization | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [reloadingUuid, setReloadingUuid] = useState<string>(uuidv4());

  const isFirstMount = React.useRef<boolean>(true);

  const refreshData = useCallback(
    async (notFoundRedirectsToHome = false): Promise<PopulatedClaim | void> => {
      setIsLoading(true);
      setReloadingUuid(uuidv4());
      try {
        const response: AxiosResponse<ClaimPolymorph> = await axios.get(`/api/v1/claims/${claimId}`);
        const populatedClaim = populateClaim(response.data);

        setClaim(populatedClaim);
        setIsLoading(false);
        return populatedClaim;
      } catch (error) {
        setIsError(true);
        const axiosError = error as AxiosError;
        if (isPermissionDeniedError(axiosError) || axiosError?.response?.status === 401) {
          history.push('/permission_denied', {});
        } else if (notFoundRedirectsToHome && axiosError?.response?.status === 404) {
          history.push('/', {});
        } else {
          await reportAxiosError(axiosError);
        }
      }
    },
    [claimId, history]
  );

  const asyncRefreshData = useCallback((): void => {
    const start = Date.now();
    const key = reloadingUuid;

    const toastTimer = setTimeout(() => {
      toast(<RefreshToaster />, {
        position: 'top-center',
        hideProgressBar: true,
        toastId: key,
        autoClose: false,
        transition: Slide,
      });
    }, 500);

    refreshData().then(() => {
      clearTimeout(toastTimer);
      const diff = Date.now() - start;
      const dismissDelay = diff < 1000 ? diff : 0;
      setTimeout(() => {
        if (toast?.isActive(key)) {
          toast.dismiss(key);
        }
      }, dismissDelay);
    });
  }, [refreshData, reloadingUuid]);

  // initial fetch & when claimId changes
  useEffect(() => {
    setClaim(null);
    setIsLoading(true);
    setIsError(false);
    if (isFirstMount.current) {
      refreshData(true);
      isFirstMount.current = false;
    } else {
      refreshData(false);
    }
    touchClaim(claimId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  if (!claim && isLoading) {
    return <LoadingIndicator isError={isError} />;
  }

  if (!claim) {
    return <LoadingIndicator isError />;
  }

  return (
    <ClaimContextProvider
      claim={claim}
      refreshData={refreshData}
      asyncRefreshData={asyncRefreshData}
      inClaimPage
      reloadingUuid={reloadingUuid}
    >
      <ClaimPageNavigationBlocker>{children}</ClaimPageNavigationBlocker>
    </ClaimContextProvider>
  );
}

function ClaimContextProvider({
  claim,
  refreshData,
  children,
  inClaimPage,
  asyncRefreshData: asyncRefreshDataFunc,
  reloadingUuid,
  createMinimizedContext = true,
}: ClaimContextProviderProps) {
  const { userOrganization } = useCms();

  claim.organization = userOrganization;

  // Handle sub-organization lookup with proper type conversion
  const subOrgId = userOrganization.sub_organizations_enabled ? claim?.policy?.sub_organization_id : null;
  if (subOrgId && userOrganization.sub_organizations && claim.policy) {
    const foundSubOrg = userOrganization.sub_organizations.find((sub_org) => sub_org.id === subOrgId);
    if (foundSubOrg) {
      // this is a workaround to avoid type errors, we should fix it when we migrate organization types
      claim.policy.sub_organization = foundSubOrg as unknown as SubOrganizationModel;
    }
  }

  const asyncRefreshData = asyncRefreshDataFunc ? asyncRefreshDataFunc : refreshData;

  return (
    <ClaimContext.Provider
      value={{
        claim,
        refreshData,
        onClaimUpdate: refreshData,
        asyncRefreshData,
        onAsyncClaimUpdate: asyncRefreshData,
        inClaimPage: inClaimPage ?? false,
        reloadingUuid: reloadingUuid ?? null,
      }}
    >
      <PolicyContextProvider policy={claim.policy}>
        <ContactsContextProvider
          // @ts-expect-error claim.contacts is not typed - fix when we migrate contact types
          contacts={claim.contacts}
          onAddContact={refreshData}
          // @ts-expect-error claim.contacts is not typed - fix when we migrate contact types
          contactsOrganizationId={claim.policy.organization_id}
          rolesClaimType={claim.lob}
        >
          {createMinimizedContext ? (
            <MinimizedDialogContextProvider>{children}</MinimizedDialogContextProvider>
          ) : (
            children
          )}
        </ContactsContextProvider>
      </PolicyContextProvider>
    </ClaimContext.Provider>
  );
}

function withClaim<P extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<
    P & {
      claim: ClaimContextType['claim'];
      onClaimUpdate: ClaimContextType['refreshData'];
      onAsyncClaimUpdate: ClaimContextType['asyncRefreshData'];
    }
  >
) {
  class WithClaim extends React.Component<Omit<P, 'claim' | 'onClaimUpdate' | 'onAsyncClaimUpdate'>> {
    static displayName = `withClaim(${getDisplayName(WrappedComponent)})`;

    render() {
      return (
        <ClaimContext.Consumer>
          {(claimContext) => (
            <WrappedComponent
              claim={claimContext.claim}
              onClaimUpdate={claimContext.refreshData}
              onAsyncClaimUpdate={claimContext.asyncRefreshData}
              {...(this.props as P)}
            />
          )}
        </ClaimContext.Consumer>
      );
    }
  }

  return WithClaim;
}

function getDisplayName<T>(WrappedComponent: React.ComponentType<T>): string {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

type UseClaimOptions = { claimRequired?: boolean };

function isClaimContext(value: unknown): value is ClaimContextType {
  return typeof value === 'object' && value !== null && 'claim' in value;
}

/**
 * useClaim hook with optional strict typing when a claim is required.
 *
 * Overloads:
 * - useClaim() → ClaimContextType
 * - useClaim({ claimRequired: false }) → ClaimContextType
 * - useClaim({ claimRequired: true }) → ClaimContextTypeRequired (throws if claim missing)
 */
function useClaim(): ClaimContextType;
function useClaim(options: { claimRequired?: false } | undefined): ClaimContextType;
function useClaim(options: { claimRequired: true }): ClaimContextTypeRequired;
function useClaim(options?: UseClaimOptions): ClaimContextType | ClaimContextTypeRequired {
  const rawContext = useContext(ClaimContext);
  const claimContext: ClaimContextType | Record<string, never> = isClaimContext(rawContext) ? rawContext : {};

  if (options?.claimRequired && !claimContext.claim) {
    reportErrorInProductionOrThrow('useClaim: claim is required but was not found in context');
  }

  return {
    claim: claimContext.claim,
    refreshData: claimContext.refreshData,
    onClaimUpdate: claimContext.refreshData,
    asyncRefreshData: claimContext.asyncRefreshData,
    onAsyncClaimUpdate: claimContext.asyncRefreshData,
    inClaimPage: claimContext.inClaimPage ?? false,
    reloadingUuid: claimContext.reloadingUuid ?? null,
  };
}

export { ClaimContextProvider, useClaim, withClaim };
export default withRouter(ClaimContainer);
